import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';
import { eUserPermissions } from 'common/Components/UserManagement/Enums/Enums';
import { permissionsDictionarySelector } from '../store/selectors/userManagementSelectors';
import { eRoleType } from 'store/slices/userSlice/';

type UserPermission = {
  value: number;
  name: string;
};

function useUserPermissions() {
  const loggedUser = useSelector((state: RootState) => state.user.loggedUser);
  const userPermissionsMap: Record<string, UserPermission> = { ...eUserPermissions };
  const permissionsDictionary: Record<string, boolean> = useSelector<RootState, Record<number, boolean>>(
    permissionsDictionarySelector
  );
  const hasPermission = useCallback(
    (permissionKey?: keyof typeof eUserPermissions) => {
      if (!permissionKey) {
        return false;
      }

      let hasGeneralPermission = false;
      if (loggedUser) {
        const { isPowerUser, permissions, userRoleType } = loggedUser;
        const permissionEntry: UserPermission = userPermissionsMap[permissionKey];
        const hasRequiredLevel = loggedUser.userRoleType == eRoleType.OPERATOR;
        const hasNonEmptyPermissions = permissions.length > 0;
        const canManageSites = permissionsDictionary[String(eUserPermissions.ManageSites.value)];
        const hasRequiredPermission = permissionsDictionary[String(permissionEntry?.value)];

        hasGeneralPermission = Boolean(
          isPowerUser ||
            (hasRequiredLevel && hasNonEmptyPermissions && canManageSites) ||
            !hasNonEmptyPermissions ||
            hasRequiredPermission
        );
      }
      return hasGeneralPermission;
    },
    [loggedUser]
  );

  return { hasPermission };
}

export default useUserPermissions;
